import {useSupportUser} from 'common/hooks/useSupportUser'
import {FC, useLayoutEffect, useRef} from 'react'
import {useIntl} from 'react-intl'

// Published on <html> while the banner is visible so viewport-sized layouts (the
// onboarding wizard's 100dvh page, fixed overlays) can make room for it. Unset
// (falls back to 0px) when there is no support session.
export const SUPPORT_ALERT_HEIGHT_VAR = '--support-alert-height'

// On selective-AI domains this is rendered ONCE at the App root (App.tsx), above
// every layout — MasterLayout, WizardLayout (onboarding), IframeLayout — and every
// route (create-password included), so a support/impersonation session is flagged
// on every screen, including the sample-data flow. Other domains render it inside
// MasterLayout as before. NOTE: local dev (<tenant>.ilmversity.test) is not a
// selective-AI domain, so outside MasterLayout the banner only appears there if
// isSelectiveAiDomains() is temporarily widened for testing. The z-index sits
// above the onboarding overlays (2600/2700) and the sample-data loader; the
// wording lives in i18n (SUPPORT_SESSION.*) so there is a single source of truth.
const SupportStaffAlert: FC = () => {
  const intl = useIntl()
  const {isSupportSession, supportUser, actingUser} = useSupportUser()
  const ref = useRef<HTMLDivElement>(null)
  const visible = isSupportSession && !!actingUser

  useLayoutEffect(() => {
    const root = document.documentElement
    const el = ref.current
    if (!visible || !el) {
      root.style.removeProperty(SUPPORT_ALERT_HEIGHT_VAR)
      return
    }
    const apply = () => root.style.setProperty(SUPPORT_ALERT_HEIGHT_VAR, `${el.offsetHeight}px`)
    apply()
    const observer = new ResizeObserver(apply)
    observer.observe(el)
    return () => {
      observer.disconnect()
      root.style.removeProperty(SUPPORT_ALERT_HEIGHT_VAR)
    }
  }, [visible])

  if (!visible || !actingUser) return null

  const supportName =
    supportUser?.username ||
    supportUser?.email ||
    intl.formatMessage({id: 'SUPPORT_SESSION.DEFAULT_SUPPORT_USER'})

  return (
    <div
      ref={ref}
      id='kt_app_support_staff_alert'
      className='alert bg-danger rounded-0 px-3 py-1 m-0 text-center support-staff-alert'
      role='alert'
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 3000,
        fontFamily: 'Inter, Helvetica, "sans-serif"',
        direction: 'ltr',
      }}
    >
      <span className='support-staff-alert__who'>
        {intl.formatMessage(
          {id: 'SUPPORT_SESSION.BANNER'},
          {
            supportUser: <strong>{supportName}</strong>,
            actingUser: (
              <strong>
                {actingUser.username} ({actingUser.email})
              </strong>
            ),
          }
        )}
      </span>{' '}
      {/* Hidden on the smallest screens (see .support-staff-alert in layout/header/_header.scss). */}
      <span className='support-staff-alert__note'>
        {intl.formatMessage({id: 'SUPPORT_SESSION.BANNER_AUDIT_NOTE'})}
      </span>
    </div>
  )
}

export {SupportStaffAlert}
